import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { MapPin, Edit, Trash2, Plus, ChevronDown, ChevronRight } from 'lucide-react';
import { StateForm } from '../../components/forms/StateForm';
import { CityForm } from '../../components/forms/CityForm';

interface State {
  id: string;
  code: string;
  name: string;
  about_template: string;
  why_choose_us_template: string;
  benefits_template: string;
  cities: City[];
}

interface City {
  id: string;
  name: string;
  slug: string;
  zip_codes: string[];
  description: string;
  county: string;
  latitude: number;
  longitude: number;
}

export function StateManagement() {
  const [states, setStates] = useState<State[]>([]);
  const [loading, setLoading] = useState(true);
  const [showStateForm, setShowStateForm] = useState(false);
  const [showCityForm, setShowCityForm] = useState<string | null>(null);
  const [editingState, setEditingState] = useState<State | null>(null);
  const [editingCity, setEditingCity] = useState<City | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedStates, setExpandedStates] = useState<Record<string, boolean>>({});

  const defaultState = {
    code: '',
    name: '',
    about_template: '',
    why_choose_us_template: '',
    benefits_template: ''
  };

  const defaultCity = {
    name: '',
    slug: '',
    zip_codes: [],
    description: '',
    county: '',
    latitude: 0,
    longitude: 0
  };

  useEffect(() => {
    loadStates();
  }, []);

  const loadStates = async () => {
    try {
      const { data: statesData, error: statesError } = await supabase
        .from('states')
        .select('*')
        .order('name');

      if (statesError) throw statesError;

      const statesWithCities = await Promise.all(
        statesData.map(async (state) => {
          const { data: citiesData, error: citiesError } = await supabase
            .from('cities')
            .select('*')
            .eq('state_id', state.id)
            .order('name');

          if (citiesError) throw citiesError;

          return {
            ...state,
            cities: citiesData || []
          };
        })
      );

      setStates(statesWithCities);
    } catch (error) {
      console.error('Error loading states:', error);
      toast.error('Failed to load states and cities');
    } finally {
      setLoading(false);
    }
  };

  const toggleStateExpansion = (stateId: string) => {
    setExpandedStates(prev => ({
      ...prev,
      [stateId]: !prev[stateId]
    }));
  };

  const handleStateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (editingState?.id) {
        // Update existing state
        const { error } = await supabase
          .from('states')
          .update({
            code: editingState.code,
            name: editingState.name,
            about_template: editingState.about_template,
            why_choose_us_template: editingState.why_choose_us_template,
            benefits_template: editingState.benefits_template
          })
          .eq('id', editingState.id);

        if (error) throw error;
        toast.success('State updated successfully');
      } else {
        // Create new state
        const { error } = await supabase
          .from('states')
          .insert([editingState || defaultState]);

        if (error) throw error;
        toast.success('State added successfully');
      }

      setShowStateForm(false);
      setEditingState(null);
      loadStates();
    } catch (error) {
      console.error('Error saving state:', error);
      toast.error('Failed to save state');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCitySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (editingCity?.id) {
        // Update existing city
        const { error } = await supabase
          .from('cities')
          .update({
            name: editingCity.name,
            slug: editingCity.slug,
            zip_codes: editingCity.zip_codes,
            description: editingCity.description,
            county: editingCity.county,
            latitude: editingCity.latitude,
            longitude: editingCity.longitude
          })
          .eq('id', editingCity.id);

        if (error) throw error;
        toast.success('City updated successfully');
      } else if (showCityForm) {
        // Create new city
        const { error } = await supabase
          .from('cities')
          .insert([{
            ...editingCity,
            state_id: showCityForm
          }]);

        if (error) throw error;
        toast.success('City added successfully');
      }

      setShowCityForm(null);
      setEditingCity(null);
      loadStates();
    } catch (error) {
      console.error('Error saving city:', error);
      toast.error('Failed to save city');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteState = async (stateId: string) => {
    if (!confirm('Are you sure you want to delete this state? This will also delete all associated cities.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('states')
        .delete()
        .eq('id', stateId);

      if (error) throw error;
      toast.success('State deleted successfully');
      loadStates();
    } catch (error) {
      console.error('Error deleting state:', error);
      toast.error('Failed to delete state');
    }
  };

  const handleDeleteCity = async (cityId: string) => {
    if (!confirm('Are you sure you want to delete this city?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('cities')
        .delete()
        .eq('id', cityId);

      if (error) throw error;
      toast.success('City deleted successfully');
      loadStates();
    } catch (error) {
      console.error('Error deleting city:', error);
      toast.error('Failed to delete city');
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">States & Cities</h2>
        <button 
          onClick={() => {
            setEditingState(defaultState);
            setShowStateForm(true);
          }}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
          <Plus className="w-5 h-5 mr-2" />
          Add State
        </button>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        {states.map(state => (
          <React.Fragment key={state.id}>
            {showStateForm && editingState?.id === state.id && (
              <div className="p-4 bg-gray-50 border-b">
                <StateForm
                  state={editingState}
                  onChange={(field, value) => setEditingState(prev => ({ ...prev!, [field]: value }))}
                  onSubmit={handleStateSubmit}
                  onCancel={() => { setShowStateForm(false); setEditingState(null); }}
                  isSubmitting={isSubmitting}
                />
              </div>
            )}
            <div key={state.id} className="border-b last:border-b-0">
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleStateExpansion(state.id)}
              >
                <div className="flex items-center">
                  {expandedStates[state.id] ? (
                    <ChevronDown className="w-5 h-5 text-gray-400 mr-2" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400 mr-2" />
                  )}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {state.name} ({state.code})
                    </h3>
                    <p className="text-sm text-gray-500">
                      {state.cities.length} cities
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingState(state);
                      setShowStateForm(true);
                      setExpandedStates(prev => ({ ...prev, [state.id]: true }));
                    }}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-full"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      await handleDeleteState(state.id);
                    }}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-full"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {expandedStates[state.id] && (
                <div className="bg-gray-50 p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-sm font-medium text-gray-700">Cities</h4>
                    <button
                      onClick={() => {
                        setEditingCity(defaultCity);
                        setShowCityForm(state.id);
                      }}
                      className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700
                      "
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add City
                    </button>
                  </div>
                  <div className="space-y-4">
                    {state.cities.map(city => (
                      <React.Fragment key={city.id}>
                        {showCityForm === state.id && editingCity?.id === city.id && (
                          <div className="bg-white p-4 rounded-lg shadow-sm">
                            <CityForm
                              city={editingCity}
                              onChange={(field, value) => setEditingCity(prev => ({ ...prev!, [field]: value }))}
                              onSubmit={handleCitySubmit}
                              onCancel={() => { setShowCityForm(null); setEditingCity(null); }}
                              isSubmitting={isSubmitting}
                            />
                          </div>
                        )}
                        <div
                          key={city.id}
                          className="bg-white p-4 rounded-lg shadow-sm"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <h5 className="font-medium text-gray-900">
                                {city.name}
                              </h5>
                              <p className="text-sm text-gray-500">
                                {city.county} County
                              </p>
                              <p className="text-sm text-gray-500">
                                ZIP Codes: {city.zip_codes.join(', ')}
                              </p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => {
                                  setEditingCity(city);
                                  setShowCityForm(state.id);
                                }}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded-full">
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteCity(city.id)}
                                className="p-1 text-red-600 hover:bg-red-50 rounded-full"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    ))}
                    {state.cities.length === 0 && (
                      <div className="text-center py-4 text-gray-500">
                        <MapPin className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                        <p>No cities added yet</p>
                      </div>
                    )}
                  </div>
                  {showCityForm === state.id && !editingCity?.id && (
                    <div className="mt-4 bg-white p-4 rounded-lg shadow-sm">
                      <CityForm
                        city={defaultCity}
                        onChange={(field, value) => setEditingCity(prev => ({ ...prev!, [field]: value }))}
                        onSubmit={handleCitySubmit}
                        onCancel={() => { setShowCityForm(null); setEditingCity(null); }}
                        isSubmitting={isSubmitting}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </React.Fragment>
        ))}
      </div>

      {showStateForm && !editingState?.id && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
            <StateForm
              state={defaultState}
              onChange={(field, value) => setEditingState(prev => ({ ...prev!, [field]: value }))}
              onSubmit={handleStateSubmit}
              onCancel={() => { setShowStateForm(false); setEditingState(null); }}
              isSubmitting={isSubmitting}
            />
          </div>
        </div>
      )}
    </div>
  );
}